import React, { PureComponent } from 'react';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  FormGroup,
  Label,
  Badge,
} from 'reactstrap';
import { fetchMyExams } from '../../redux/actions/inscripcionExamenActions';
import { getMyExamReviewRequests } from '../../redux/actions/examReviewRequestActions';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import { createErrorMessage } from '../../helpers/notification';
import { dateTimeFormat } from '../../helpers/dateFormat';
import renderSelectField from '../../shared/components/form/Select';

class StudentExamReviewRequestsContainer extends PureComponent {
  static propTypes = {
    change: PropTypes.func.isRequired,
    history: PropTypes.shape({}).isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      examOptions: [],
      reviewRequests: [],
    };
  }

  componentDidMount() {
    this.loadInitialData();
  }

  loadInitialData = () => {
    const { change } = this.props;
    this.setState({ loading: true });

    fetchMyExams({
      pageInfo: {
        page: 1,
        pageSize: 100,
        filters: {},
      },
      onSuccess: ({ items }) => {
        const mapByExamId = {};
        (items || []).forEach((item) => {
          if (!mapByExamId[item.examenId]) {
            mapByExamId[item.examenId] = {
              examenId: item.examenId,
              label: `${item.codMateria}-${item.materia} ${item.periodo} (${new Date(
                item.fechaExamen,
              ).toLocaleDateString('en-GB')})`,
            };
          }
        });

        const examOptions = Object.values(mapByExamId);
        const firstOption = { value: '', label: 'Todos' };

        this.setState({
          examOptions,
          loading: false,
        });
        change('examenFilter', firstOption);

        this.loadReviewRequests(null);
      },
      onError: () => {
        this.setState({ loading: false, examOptions: [], reviewRequests: [] });
        createErrorMessage('Error al intentar cargar los exámenes del alumno.');
      },
    });
  };

  loadReviewRequests = (examId) => {
    this.setState({ loading: true });

    const parsedExamId = examId ? +examId : null;
    getMyExamReviewRequests(parsedExamId, {
      onSuccess: (reviewRequests) => {
        this.setState({
          reviewRequests: reviewRequests || [],
          loading: false,
        });
      },
      onError: () => {
        this.setState({ loading: false, reviewRequests: [] });
        createErrorMessage('Error al intentar cargar tus solicitudes de revisión.');
      },
    });
  };

  onFilterExamChange = (option) => {
    const value = option && typeof option.value !== 'undefined' ? option.value : '';
    this.loadReviewRequests(value || null);
  };

  onBack = () => {
    const { history } = this.props;
    history.push('/pages/student-exams');
  };

  getStatusColor = (status) => {
    if (status === 0) return 'warning';
    if (status === 1) return 'info';
    if (status === 2) return 'success';
    return 'secondary';
  };

  getStatusLabel = (status) => {
    if (status === 0) return 'Pendiente';
    if (status === 1) return 'En revisión';
    if (status === 2) return 'Resuelta';
    return '-';
  };

  getExamFilterOptions = () => {
    const { examOptions } = this.state;
    return [
      { value: '', label: 'Todos' },
      ...examOptions.map(opt => ({ value: `${opt.examenId}`, label: opt.label })),
    ];
  };

  renderReviewRequestCard = reviewRequest => (
    <Col
      className="pb-4"
      sm={12}
      md={6}
      lg={4}
      xl={3}
      key={`review-${reviewRequest.id}`}
    >
      <div style={{ display: 'grid' }}>
        <Card
          outline
          color="primary"
          className="card-subject pb-0"
        >
          <CardBody className={`summary-card summary-card--${this.getStatusColor(reviewRequest.estado)}`}>
            <h5
              className="summary-card__title"
              style={{
                display: 'inline-flex',
                flexWrap: 'wrap',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: '0.5rem',
                width: '100%',
                marginBottom: '0',
              }}
            >
              <Badge
                style={{ fontSize: '0.81rem' }}
                color={this.getStatusColor(reviewRequest.estado)}
                pill
              >
                {this.getStatusLabel(reviewRequest.estado)}
              </Badge>
              <Badge
                style={{ fontSize: '0.81rem' }}
                color={this.getStatusColor(reviewRequest.estado)}
                pill
              >
                # {reviewRequest.id}
              </Badge>
            </h5>

            <div className="summary-card__body checklist-card">
              <h4 className="summary-card__title" style={{ marginBottom: '0' }}>
                {reviewRequest.materia}
              </h4>
              <em style={{ fontSize: '12px', color: 'slategray' }}>
                {`Periodo ${reviewRequest.periodo}`}
              </em>
              <hr />

              <Row dir="ltr" className="summary-card__item">
                <Col md={12}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span>
                      <strong>Examen:</strong>{' '}
                      {new Date(reviewRequest.fechaExamen).toLocaleDateString('en-GB')}
                    </span>
                    <span>
                      <strong>Solicitud:</strong> {dateTimeFormat(reviewRequest.fechaSolicitud)}
                    </span>
                    <span>
                      <strong>Resolución:</strong>{' '}
                      {reviewRequest.fechaResolucion ? dateTimeFormat(reviewRequest.fechaResolucion) : '-'}
                    </span>
                  </div>
                </Col>
              </Row>

              <Row dir="ltr" className="summary-card__item">
                <Col md={12}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span><strong>Motivo:</strong> {reviewRequest.motivoReclamo}</span>
                    <span><strong>Respuesta:</strong> {reviewRequest.respuestaDocente || '-'}</span>
                  </div>
                </Col>
              </Row>
            </div>
          </CardBody>
        </Card>
      </div>
    </Col>
  );

  render() {
    const {
      loading,
      reviewRequests,
    } = this.state;
    const examFilterOptions = this.getExamFilterOptions();

    if (loading) {
      return <LoadingSpinner />;
    }

    return (
      <Container>
        <Row>
          <Col md={12}>
            <h3 className="page-title">Mis solicitudes de revisión</h3>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Card>
              <CardBody>
                <FormGroup>
                  <Label for="examenFilter">Filtrar por examen</Label>
                  <Field
                    name="examenFilter"
                    component={renderSelectField}
                    placeholder="Filtrar por examen"
                    options={examFilterOptions}
                    onChange={this.onFilterExamChange}
                    addDefaultOption={false}
                  />
                </FormGroup>

                <Container className="summary-container">
                  <Row>
                    {reviewRequests.map(this.renderReviewRequestCard)}
                    {!reviewRequests.length && (
                      <Col xs={12}>
                        <Card>
                          <CardBody className="text-center">
                            No tienes solicitudes de revisión para el filtro seleccionado.
                          </CardBody>
                        </Card>
                      </Col>
                    )}
                  </Row>
                </Container>

                <div className="card__title">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={this.onBack}
                  >
                    Volver
                  </button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }
}

export default reduxForm({
  form: 'studentExamReviewRequestsFilterForm',
  enableReinitialize: true,
})(withRouter(StudentExamReviewRequestsContainer));
